import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type { components } from '@/api/schema';
import { Roles } from '@/auth/roles';
import { useCan, useTenantId } from '@/auth/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Card, Skeleton } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section, Textarea } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { parseJson, prettyJson } from '@/lib/json';
import { cn } from '@/lib/utils';
import { parseOutcome, rulesetSchema, rulesFromJson, rulesToJson, type RulesOutcome, type RulesetForm } from './rules';

type RulesetDto = components['schemas']['RulesetDto'];
const NEW = '__new__';

export function RulesetsPage() {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const canEdit = useCan({ roles: [Roles.PlatformAdmin, Roles.TenantAdmin, Roles.TenantBuilder] });
  const isPlatformAdmin = useCan({ roles: [Roles.PlatformAdmin] });
  const list = useQuery({
    queryKey: queryKeys.scoped(tenantId, 'rulesets'),
    queryFn: async () => unwrap(await api.GET('/v1/rulesets')),
  });
  const [selected, setSelected] = useState<string | null>(null);
  const items = list.data?.items ?? [];
  const current = selected === NEW ? null : (items.find((r) => (r.id ?? r.key) === selected) ?? null);

  return (
    <div className="flex h-full flex-col">
      <PageHeader help="rulesets"
        title={t('admin.rulesets.title')}
        description={t('admin.rulesets.description')}
        actions={
          canEdit && (
            <Button onClick={() => setSelected(NEW)}>
              <Plus /> {t('admin.rulesets.create')}
            </Button>
          )
        }
      />
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <div className="overflow-auto border-r p-3">
          {list.isPending && <Skeleton className="h-32 w-full" />}
          <ul className="flex flex-col gap-0.5">
            {items.map((r) => {
              const key = r.id ?? r.key;
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => setSelected(key)}
                    className={cn('flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left hover:bg-muted', selected === key && 'bg-muted')}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{r.name}</span>
                      <span className="block truncate font-mono text-xs text-muted-foreground">{r.key}</span>
                    </span>
                    {r.kind === 'builtin' ? <Lock className="size-3 shrink-0 text-muted-foreground" aria-label={t('admin.rulesets.readOnly')} /> : <Badge>{r.scope}</Badge>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="overflow-auto p-6">
          {selected === null && <p className="text-sm text-muted-foreground">{t('admin.rulesets.selectHint')}</p>}
          {selected !== null && (
            <RulesetEditor
              key={selected}
              ruleset={current}
              readOnly={!canEdit || current?.kind === 'builtin' || (current?.scope === 'platform' && !isPlatformAdmin)}
              onSaved={(id) => setSelected(id)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const emptyRule = { id: '', when: '', assert: '', severity: 'error' as const, message: '', path: '' };

function RulesetEditor({ ruleset, readOnly, onSaved }: { ruleset: RulesetDto | null; readOnly: boolean; onSaved: (id: string) => void }) {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const qc = useQueryClient();
  const isBuiltIn = ruleset?.kind === 'builtin';
  const form = useForm<RulesetForm>({
    resolver: zodResolver(rulesetSchema(t)),
    defaultValues: ruleset
      ? { key: ruleset.key, name: ruleset.name, description: ruleset.description ?? '', rules: rulesFromJson(ruleset.rules) }
      : { key: '', name: '', description: '', rules: [{ ...emptyRule }] },
  });
  const rules = useFieldArray({ control: form.control, name: 'rules' });

  const save = useMutation({
    meta: { silent: true },
    mutationFn: async (v: RulesetForm) => {
      const body = rulesToJson(v.rules) as never;
      if (ruleset?.id) {
        return unwrap(
          await api.PUT('/v1/rulesets/{id}', {
            params: { path: { id: ruleset.id } },
            body: { name: v.name, description: v.description || null, rules: body },
          }),
        );
      }
      const created = unwrap(await api.POST('/v1/rulesets', { body: { key: v.key, name: v.name, description: v.description || null, rules: body, scope: null } }));
      return created as unknown as RulesetDto | undefined;
    },
  });

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const saved = await save.mutateAsync(v);
      await qc.invalidateQueries({ queryKey: queryKeys.scoped(tenantId, 'rulesets') });
      toast.success(t('admin.common.saved'));
      if (saved?.id) onSaved(saved.id);
    } catch (err) {
      applyProblemToForm(form, err, {
        fields: ['key', 'name', 'description', 'rules', ...rules.fields.flatMap((_, i) => ['id', 'when', 'assert', 'message', 'path'].map((f) => `rules.${i}.${f}`))],
      });
    }
  });
  const { errors, isSubmitting } = form.formState;

  return (
    <div className="flex flex-col gap-8">
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="grid max-w-2xl grid-cols-2 gap-3">
          <Field label={t('admin.rulesets.key')} htmlFor="rs-key" error={errors.key?.message} hint={t('admin.rulesets.keyHint')}>
            <Input id="rs-key" className="font-mono" disabled={!!ruleset || readOnly} aria-invalid={!!errors.key} {...form.register('key')} />
          </Field>
          <Field label={t('admin.rulesets.name')} htmlFor="rs-name" error={errors.name?.message}>
            <Input id="rs-name" disabled={readOnly} aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field className="col-span-2" label={t('admin.rulesets.descriptionLabel')} htmlFor="rs-desc">
            <Input id="rs-desc" disabled={readOnly} {...form.register('description')} />
          </Field>
        </div>

        {isBuiltIn ? (
          <p className="text-sm text-muted-foreground">{t('admin.rulesets.builtInHint')}</p>
        ) : (
          <Section
            title={t('admin.rulesets.rules')}
            actions={
              !readOnly && (
                <Button type="button" size="sm" variant="outline" onClick={() => rules.append({ ...emptyRule })}>
                  <Plus /> {t('admin.rulesets.addRule')}
                </Button>
              )
            }
          >
            {errors.rules?.message && <p className="text-xs text-destructive">{errors.rules.message}</p>}
            {rules.fields.map((f, i) => {
              const e = errors.rules?.[i];
              return (
                <Card key={f.id} className="grid grid-cols-6 gap-2 p-3">
                  <Field className="col-span-2" label={t('admin.rulesets.ruleId')} htmlFor={`r-${i}-id`} error={e?.id?.message}>
                    <Input id={`r-${i}-id`} className="font-mono" disabled={readOnly} {...form.register(`rules.${i}.id`)} />
                  </Field>
                  <Field className="col-span-2" label={t('admin.rulesets.severity')} htmlFor={`r-${i}-sev`}>
                    <NativeSelect id={`r-${i}-sev`} disabled={readOnly} {...form.register(`rules.${i}.severity`)}>
                      <option value="error">{t('admin.rulesets.severityError')}</option>
                      <option value="flag">{t('admin.rulesets.severityFlag')}</option>
                    </NativeSelect>
                  </Field>
                  <Field className="col-span-2" label={t('admin.rulesets.path')} htmlFor={`r-${i}-path`}>
                    <Input id={`r-${i}-path`} className="font-mono" disabled={readOnly} {...form.register(`rules.${i}.path`)} />
                  </Field>
                  <Field className="col-span-3" label={t('admin.rulesets.when')} htmlFor={`r-${i}-when`} hint={t('admin.rulesets.whenHint')}>
                    <Input id={`r-${i}-when`} className="font-mono text-xs" disabled={readOnly} {...form.register(`rules.${i}.when`)} />
                  </Field>
                  <Field className="col-span-3" label={t('admin.rulesets.assert')} htmlFor={`r-${i}-assert`} error={e?.assert?.message}>
                    <Input id={`r-${i}-assert`} className="font-mono text-xs" disabled={readOnly} {...form.register(`rules.${i}.assert`)} />
                  </Field>
                  <Field className="col-span-5" label={t('admin.rulesets.message')} htmlFor={`r-${i}-msg`} error={e?.message?.message}>
                    <Input id={`r-${i}-msg`} disabled={readOnly} {...form.register(`rules.${i}.message`)} />
                  </Field>
                  {!readOnly && (
                    <div className="flex items-end justify-end">
                      <Button type="button" size="icon" variant="ghost" aria-label={t('admin.rulesets.removeRule')} onClick={() => rules.remove(i)}>
                        <Trash2 />
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </Section>
        )}
        <RootError message={errors.root?.message} />
        {!readOnly && (
          <div>
            <Button type="submit" disabled={isSubmitting}>
              {t('admin.common.save')}
            </Button>
          </div>
        )}
      </form>
      {ruleset && <RulesetTester rulesetId={ruleset.id ?? ruleset.key} />}
    </div>
  );
}

function RulesetTester({ rulesetId }: { rulesetId: string }) {
  const { t } = useTranslation();
  const [input, setInput] = useState(() => prettyJson({ ahvNumber: '756.1234.5678.97', birthDate: '1991-03-12' }));
  const [parseError, setParseError] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async (value: unknown) => {
      const res = await api.POST('/v1/rulesets/{id}/test', { params: { path: { id: rulesetId } }, body: { input: value as never } });
      return parseOutcome(unwrap(res) as unknown);
    },
  });
  const submit = () => {
    const parsed = parseJson(input);
    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }
    setParseError(null);
    run.mutate(parsed.value);
  };
  return (
    <Section title={t('admin.rulesets.tester')} description={t('admin.rulesets.testerHint')}>
      <Textarea rows={8} className="font-mono text-xs" value={input} onChange={(e) => setInput(e.target.value)} spellCheck={false} aria-label={t('admin.rulesets.testInput')} />
      <RootError message={parseError ? `${t('admin.validation.json')}: ${parseError}` : undefined} />
      <div>
        <Button size="sm" onClick={submit} disabled={run.isPending}>
          {t('admin.rulesets.runTest')}
        </Button>
      </div>
      {run.data && <OutcomeView outcome={run.data} />}
    </Section>
  );
}

export function OutcomeView({ outcome }: { outcome: RulesOutcome }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex flex-col gap-1 rounded-md border p-3 text-sm" data-testid="rules-outcome">
      <div>{outcome.ok ? <Badge variant="success">{t('admin.rulesets.ok')}</Badge> : <Badge variant="destructive">{t('admin.rulesets.failed')}</Badge>}</div>
      {outcome.errors.map((e) => (
        <div key={`e-${e.id}`} className="text-xs text-destructive">
          <span className="font-mono">{e.id}</span>: {e.message} {e.path && <span className="text-muted-foreground">({e.path})</span>}
        </div>
      ))}
      {outcome.flags.map((f) => (
        <div key={`f-${f.id}`} className="text-xs">
          <Badge variant="warning">{t('admin.rulesets.flag')}</Badge> <span className="font-mono">{f.id}</span>: {f.message}
        </div>
      ))}
    </div>
  );
}
